/**
 * Activity Logger - Helper para registrar actividades del usuario desde el frontend
 * 
 * ⚡ OPTIMIZACIÓN: Solo se registran acciones importantes (descargas, modificaciones)
 * No se registran vistas (GET) para evitar saturar la base de datos
 */

// Usar variable de entorno para la URL de la API
const API_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'development' ? 'http://localhost:4007' : '');

const ACTIVITY_LOG_URL = `${API_URL}/api/activity-logs/log`;

/**
 * Configuración de logging
 * Habilita/deshabilita tipos de logs para optimizar rendimiento
 */
const LOGGING_CONFIG = {
  enabled: true,                    // Master switch
  logViews: false,                  // ❌ No loguear visualizaciones (GET)
  logDownloads: true,               // ✅ Loguear descargas
  logModifications: true,           // ✅ Loguear modificaciones (POST/PUT/DELETE)
  logNavigation: false,             // ❌ No loguear navegación
  logModalOpen: false,              // ❌ No loguear apertura de modales
  logButtonClicks: false,           // ❌ No loguear clicks de botones
  logAuth: true,                    // ✅ Loguear login/logout
};

/**
 * Registra una actividad del usuario
 * @param {string} action - Nombre de la acción (ej: 'recibo_download', 'beneficio_view')
 * @param {object} metadata - Información adicional (opcional)
 * @param {boolean} forceLog - Forzar el log aunque esté deshabilitado
 * @returns {Promise<void>}
 */
export const logActivity = async (action, metadata = {}, forceLog = false) => {
  try {
    // No loguear si está deshabilitado globalmente
    if (!LOGGING_CONFIG.enabled && !forceLog) {
      return;
    }
    // Obtener token del localStorage o sessionStorage
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    
    if (!token) {
      console.warn('No hay token disponible para registrar actividad');
      return;
    }

    await fetch(ACTIVITY_LOG_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        action,
        metadata
      })
    });
  } catch (error) {
    // Silent fail - no queremos romper la experiencia del usuario si falla el logging
    console.error('Error al registrar actividad:', error);
  }
};

/**
 * Presets comunes para acciones frecuentes
 */
export const activityPresets = {
  /**
   * Registra la vista de un documento/recurso
   * ⚡ DESHABILITADO por defecto para optimizar rendimiento
   */
  view: (entityType, entityId, extraMetadata = {}) => {
    if (!LOGGING_CONFIG.logViews) {
      return Promise.resolve(); // No hacer nada
    }
    return logActivity(`${entityType}_view`, {
      entityType,
      entityId,
      ...extraMetadata
    });
  },

  /**
   * Registra una descarga
   * ✅ HABILITADO - Acción importante
   */
  download: (entityType, entityId, fileName = null, extraMetadata = {}) => {
    if (!LOGGING_CONFIG.logDownloads) {
      return Promise.resolve();
    }
    return logActivity(`${entityType}_download`, {
      entityType,
      entityId,
      fileName,
      ...extraMetadata
    });
  },

  /**
   * Registra un click en un botón importante
   * ⚡ DESHABILITADO por defecto
   */
  buttonClick: (buttonName, section, extraMetadata = {}) => {
    if (!LOGGING_CONFIG.logButtonClicks) {
      return Promise.resolve();
    }
    return logActivity('button_click', {
      buttonName,
      section,
      ...extraMetadata
    });
  },

  /**
   * Registra navegación entre páginas
   * ⚡ DESHABILITADO por defecto
   */
  navigation: (from, to, extraMetadata = {}) => {
    if (!LOGGING_CONFIG.logNavigation) {
      return Promise.resolve();
    }
    return logActivity('page_navigation', {
      from,
      to,
      ...extraMetadata
    });
  },

  /**
   * Registra apertura de modal/ventana
   * ⚡ DESHABILITADO por defecto
   */
  openModal: (modalName, context, extraMetadata = {}) => {
    if (!LOGGING_CONFIG.logModalOpen) {
      return Promise.resolve();
    }
    return logActivity('modal_open', {
      modalName,
      context,
      ...extraMetadata
    });
  }
};

export default {
  logActivity,
  ...activityPresets,
  // Exportar configuración para poder modificarla si es necesario
  config: LOGGING_CONFIG
};
